/* eslint-disable camelcase */
import { isLightOrDark } from 'src/services/color';
import {
  matDirectionsCar as directions_car,
  matGpsFixed as gps_fixed,
  matLabel as label,
  matLens as lens,
  matStarRate as star_rate,
  matStop as stop,
  matFlag as flag,
  matPushPin as push_pin,
  matAdjust as adjust,
  matCenterFocusStrong as center_focus_strong,
  matControlCamera as control_camera,
  matCategory as category,
  matFontDownload as font_download,
  matAttachMoney as attach_money,
  matMonetizationOn as monetization_on,
  matBusinessCenter as business_center,
  matDeveloperBoard as developer_board,
  matFlare as flare,
  matTour as tour,
  matGpsNotFixed as gps_not_fixed,
  matLaptop as laptop,
  matMemory as memory,
  matRouter as router,
  matSettingsInputAntenna as settings_input_antenna,
  matSettingsRemote as settings_remote,
  matSpeakerPhone as speaker_phone,
  matStars as stars,
  matInsertEmoticon as insert_emoticon,
  matAgriculture as agriculture,
  matAirplanemodeActive as airplanemode_active,
  matAudiotrack as audiotrack,
  matBikeScooter as bike_scooter,
  matCamera as camera,
  matCameraAlt as camera_alt,
  matCarpenter as carpenter,
  matConstruction as construction,
  matDirectionsBike as directions_bike,
  matDirectionsBoat as directions_boat,
  matEventSeat as event_seat,
  matHomeRepairService as home_repair_service,
  matLock as lock,
  matSecurity as security,
  matSupport as support,
  matToys as toys,
  matTwoWheeler as two_wheeler,
  matWork as work,
  matWeekend as weekend,
} from '@quasar/extras/material-icons';
import { ASSET_TYPES, ASSET_ICONS } from 'src/services/constants';
import pinAsset from 'src/assets/pin-asset.raw.svg';
import SvgMarker from './SvgMarker';

const materialIcons = {
  directions_car,
  gps_fixed,
  label,
  lens,
  star_rate,
  stop,
  flag,
  push_pin,
  adjust,
  center_focus_strong,
  control_camera,
  category,
  font_download,
  attach_money,
  monetization_on,
  business_center,
  developer_board,
  flare,
  tour,
  gps_not_fixed,
  laptop,
  memory,
  router,
  settings_input_antenna,
  settings_remote,
  speaker_phone,
  stars,
  insert_emoticon,
  agriculture,
  airplanemode_active,
  audiotrack,
  bike_scooter,
  camera,
  camera_alt,
  carpenter,
  construction,
  directions_bike,
  directions_boat,
  event_seat,
  home_repair_service,
  lock,
  security,
  support,
  toys,
  two_wheeler,
  work,
  weekend,
};

export default class AssetSvgMarker extends SvgMarker {
  static PIN_SVG = `<g>
     <g transform="translate(2,2)">
        <path id="pin" d="M28 84C34.553993 64.751295 39.198861 54.333741 41.934604 52.747339 50.339874 47.873291 56 38.725358 56 28.243916 56 12.645232 43.463973 0 28 0 12.536027 0 0 12.645232 0 28.243916 0 38.958397 5.9146121 48.279412 14.630069 53.066069 17.2787 54.520736 21.735344 64.832046 28 84Z" stroke="#ffffff" stroke-width="3" fill="#00C1DE"></path>
     </g>
  </g>`;

  static OUTLINE_SVG = `<g>
    <g transform="translate(2,2)">
      <path fill="#000000" stroke-width="4" stroke="#000000" d="M28 84C34.553993 64.751295 39.198861 54.333741 41.934604 52.747339 50.339874 47.873291 56 38.725358 56 28.243916 56 12.645232 43.463973 0 28 0 12.536027 0 0 12.645232 0 28.243916 0 38.958397 5.9146121 48.279412 14.630069 53.066069 17.2787 54.520736 21.735344 64.832046 28 84Z"></path>
    </g>
  </g>`;

  /** @type {String} */
  iconUri = null;

  constructor(iconUri) {
    super(pinAsset, {
      height: 91,
      width: 60,
      innerColor: '#fff',
      outerColor: '#00C1DE', // TODO: replace these with Quasar variables
    });
    this.iconUri = iconUri;
  }

  /**
   * Creates and loads an HTML Image from the SVG.
   *
   * @param {String} colorOverride
   * @param {Boolean} outline
   * @returns {Promise<Image>}
   */
  toImage(colorOverride, outline) {
    return new Promise((resolve) => {
      const iconUri = this.iconUri || ASSET_ICONS[ASSET_TYPES.ASSET];
      const iconCode = materialIcons[iconUri];

      const image = new Image(this.width, this.height);
      image.onload = () => {
        resolve(image);
      };

      image.src = `data:image/svg+xml;base64,${btoa(AssetSvgMarker.getIconSvg(iconCode, colorOverride, outline))}`;
    });
  }

  /**
   * Converts a given iconCode into an <svg>.
   *
   * @param {String} iconCode
   * @param {String} colorOverride
   * @param {Boolean} outline
   * @returns {String}
   */
  static getIconSvg(iconCode, colorOverride, outline) {
    const [def] = iconCode.split('|');

    // <svg>
    const svgEl = document.createElement('svg');
    svgEl.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    svgEl.setAttribute('viewbox', '0 0 60 91');
    svgEl.setAttribute('height', '91px'); // needed for Firefox
    svgEl.setAttribute('width', '60px'); // needed for Firefox

    // outline <svg>
    if (outline) {
      const outlineSvg = document.createElement('svg');
      outlineSvg.innerHTML = AssetSvgMarker.OUTLINE_SVG;
      svgEl.appendChild(outlineSvg.childNodes[0]);
    }

    // pin <svg>
    const pinSvg = document.createElement('svg');
    pinSvg.innerHTML = AssetSvgMarker.PIN_SVG;
    svgEl.appendChild(pinSvg.childNodes[0]);

    // Handle marker colors
    let iconColor = 'white';
    if (colorOverride && colorOverride !== 'none') {
      iconColor = isLightOrDark(colorOverride) === 'light' ? 'black' : 'white';
      svgEl.querySelector('#pin').setAttribute('fill', colorOverride);
    }

    // <g>
    const g = document.createElement('g');
    g.setAttribute('style', 'transform:scale(1.8) translate(4.6px, 4.6px)');

    def.split('&&').forEach((path) => {
      const [d, style, transform] = path.split('@@');

      // <path>
      const pathEl = document.createElement('path');
      pathEl.setAttribute('d', d);
      pathEl.setAttribute('fill', iconColor);

      if (style) {
        pathEl.setAttribute('style', style);
      }

      if (transform) {
        pathEl.setAttribute('transform', transform);
      }

      g.appendChild(pathEl);
    });

    svgEl.appendChild(g);

    return svgEl.outerHTML;
  }
}
